
import { Component, Prop, Watch } from 'vue-property-decorator';
import WizardContentView
  from '@/ui/components/wizards/baseComponents/WizardContentView.vue';
import { mixins } from 'vue-class-component';
import { Validation } from '@/ui/mixins/validation';
import { Getter, State } from 'vuex-class';
import InfoTooltip from '@/ui/components/components/InfoTooltip.vue';
import { ITenantWizardState } from '@/store/modules/tenantWizard/types';
import { cloneDeep } from 'lodash';
import { IMember } from '@/types/members.types';
import { IFlatsConfig } from '@/types/wizards/tenant.types';
import { PlcPerformanceCategorie } from '@/utils/plcPerformanceLimits';
import { IProject } from '@/types/project.types';
import { plcLevelVersionDate, plcVersionDate } from '@/utils/versionManagementUtils';

@Component({
  components: {
    WizardContentView,
    InfoTooltip,
  },
})
export default class FlatDetails extends mixins(Validation) {
  @Prop({ default: true }) showNumberOfFlats!: boolean;
  @Prop({ default: true }) showTitle!: boolean;
  @Prop({ default: false }) showBuildingAddress!: boolean;
  @Prop({ default: false }) showRenterSwapInfo!: boolean;
  @State('tenantWizard') tenantState!: ITenantWizardState;
  @Getter('members/members') members!: IMember[];
  @Getter('projects/project') project!: IProject;
  @Getter('projects/projectCreationTimestamp') projectCreationTimestamp!: number;

  indexChanged: any[] = [];
  valid = false;
  isInitialLoadingDone = false;
  buildingAddress = {
    clientName: '',
    street: '',
    zip: '',
    city: '',
    country: '',
  };

  flatsConfigList: IFlatsConfig[] = [
    {
      name: '',
      mail: '',
      clientId: '',
      address: {
        clientName: '',
        street: '',
        zip: '',
        city: '',
        country: '',
        houseNumber: '',
      },
      additionalText: this.$t('tenantWizard.flatsInformation.defaultAdditionalText') as string,
      sideCostPercentage: 0,
      hasChargingStation: false,
      counters: {
        energyCounter: [],
        heatingCounter: [],
        serviceWaterCounter: [],
        coldWaterCounter: [],
        coolingCounter: [],
      },
    },
  ]
  flatsCount = 1;
  oldFlatsCount = 1;
  saved = true;
  currentFlatConfig: any = undefined;

  get plcLevelVersionDateFeature() {
    return plcVersionDate(this.project).getTime() > plcLevelVersionDate.getTime();
  }

  get flatLimit() {
    let returnValue = 20;
    // TODO: remove this when PLC performance level is implemented on SPS side
    // if (this.plcLevelVersionDateFeature && this.project.meta.controller.plcPerformanceLevel === PlcPerformanceCategorie.high) {
    //   // if the project has the high performance level and the plcLevelVersionDateFeature is enabled, the limit is 150
    //   returnValue = 150;
    // }
    return returnValue;
  }

  // checks if all flats are filled
  get allFlatsFilled() {
    const statusList = this.flatsConfigList.map((value: IFlatsConfig, index: number) => {
      return this.hasEmptyValue(index);
    });
    return statusList.every(element => element === false);
  }

  /**
   * returns a array of all available member mails
   */
  get availableMemberMails() {
    return this.members.map((member: IMember) => member.email).filter((email: string) => !email.includes('support'));
  }

  panels: any = [];

  @Watch('panels')
  updatePanels(newVal: any, oldVal: any) {
    if (this.showNumberOfFlats) {
      if (newVal) {
        this.handleClickPanelHeader();
      }
      return;
    }
    const defaultFlatConfig = {
      name: '',
      mail: '',
      clientId: '',
      address: {
        clientName: '',
        street: '',
        zip: '',
        city: '',
        country: '',
        houseNumber: '',
      },
      additionalText: this.$t('tenantWizard.flatsInformation.defaultAdditionalText') as string,
      sideCostPercentage: 0,
      counters: {
        energyCounter: [],
        heatingCounter: [],
        serviceWaterCounter: [],
        coldWaterCounter: [],
      },
    };

    if (newVal) {
      // update the currentFlatConfig when panel is opened to then compare if it was changed
      this.currentFlatConfig = this.flatsConfigList[newVal[0]];
      // trigger validation for textfields when panel is opened
      this.handleClickPanelHeader();
    } else {
      // update config with default values when panel is closed
      this.currentFlatConfig = defaultFlatConfig;
    }

    // set saved to true
    this.saved = true;
  }

  @Watch('valid')
  updateValid() {
    if (this.allFlatsFilled === false) {
      this.$emit('updateValid', { status: false });
      return;
    }
    this.$emit('updateValid', { status: this.valid });
  }

  // emits the flatsConfigList to the parent component on input action of any field
  handleInput(index = null) {
    if (this.currentFlatConfig !== this.flatsConfigList[this.panels]) {
      this.saved = false;
    } else {
      this.saved = true;
    }
    if (index !== null) {
      if (!this.indexChanged.includes(index)) {
        this.indexChanged.push(index);
      }
    }
    // case inside when going through the tenant wizard, there it is not relevant to check inital loading
    if (this.showNumberOfFlats) {
      this.$emit('updateFlats', { flats: this.flatsConfigList });
    }
    if (this.isInitialLoadingDone === true) {
      this.$emit('updateFlats', { flats: this.flatsConfigList });
    }
  }

  handleBuildingAddressChange() {
    this.$emit('updateBuildingAddress', { buildingAddress: this.buildingAddress });
    // case inside when going through the tenant wizard, there it is not relevant to check inital loading
    if (this.showNumberOfFlats) {
      this.$emit('updateFlats', { flats: this.flatsConfigList });
    }
    if (this.isInitialLoadingDone === true) {
      this.$emit('updateFlats', { flats: this.flatsConfigList });
    }
  }

  // adds or removes flats based on the input value of the v-select
  handleChange() {
    const newValue = this.flatsCount;
    const oldValue = this.oldFlatsCount;
    const valueDifference = newValue - oldValue;
    if (valueDifference > 0) {
      for (let index = 0; index < valueDifference; index++) {
        this.flatsConfigList.push({
          name: '',
          mail: '',
          clientId: '',
          address: {
            clientName: '',
            street: '',
            zip: '',
            city: '',
            country: '',
            houseNumber: '',
          },
          additionalText: this.$t('tenantWizard.flatsInformation.defaultAdditionalText') as string,
          sideCostPercentage: 0,
          hasChargingStation: false,
          counters: {
            energyCounter: [],
            heatingCounter: [],
            serviceWaterCounter: [],
            coldWaterCounter: [],
            coolingCounter: [],
          },
        });
      }
      this.valid = false;
    } else if (valueDifference < 0) {
      this.flatsConfigList = this.flatsConfigList.slice(0, valueDifference);
    }
    this.oldFlatsCount = this.flatsCount;
    this.valid = this.allFlatsFilled;
    this.$emit('updateFlats', { flats: this.flatsConfigList });
  }

  mapFlatConfigList() {
    const tenantFlatsCopy = this.tenantState.tenant.general.flats;

    // fill up building address based on saved flat address
    this.buildingAddress.city = tenantFlatsCopy.flatConfigurations[0].address.city;
    this.buildingAddress.country = tenantFlatsCopy.flatConfigurations[0].address.country;
    this.buildingAddress.zip = tenantFlatsCopy.flatConfigurations[0].address.zip;
    // eslint-disable-next-line prefer-destructuring
    this.buildingAddress.street = tenantFlatsCopy.flatConfigurations[0].address.street.split(',')[0];

    // remap housenumber from street to specific houseNumber field in address
    this.flatsConfigList.forEach((flat: any) => {
      // eslint-disable-next-line prefer-destructuring
      flat.address.houseNumber = flat.address.street.split(', ')[1];
      // eslint-disable-next-line prefer-destructuring
      flat.address.street = flat.address.street.split(',')[0];
    });
  }

  created() {
    if (this.tenantState.tenant.general.flats.flatConfigurations.length !== 0) {
      this.flatsCount = this.tenantState.tenant.general.flats.flatCount;
      this.oldFlatsCount = this.tenantState.tenant.general.flats.flatCount;
      this.flatsConfigList = cloneDeep(this.tenantState.tenant.general.flats.flatConfigurations);
      this.mapFlatConfigList();
    } else {
      this.valid = false;
    }
  }

  async mounted() {
    await this.$nextTick(() => {
      (this.$refs.form as any).validate();
    });
    this.isInitialLoadingDone = true;
  }

  // will trigger validation of the form when the panel header is clicked
  // timeout is needed in order for it to work correctly on first open of the panel
  handleClickPanelHeader() {
    setTimeout(() => {
      if (this.$refs.form === undefined) {
        return;
      }
      (this.$refs.form as any).validate();
    }, 300);
  }

  // checks if all values of a flat are filled
  hasEmptyValue(index: number) {
    const { name, mail, clientId, address } = this.flatsConfigList[index];
    const { clientName, houseNumber } = address;

    const variablesToCheck = [
      name,
      mail,
      clientId,
      clientName,
      houseNumber?.toString() || '',
    ];

    return variablesToCheck.some(value => value === '');
  }
}
